// src/views/layout/Fxbazzar.js — main shell: market watch, sidebar pages
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import '../../assets/scss/fxbazzar.scss';
import { FONT_ROBOTO } from '../../utils/fonts';
import TradingAccountsModal from '../../components/account/TradingAccountsModal/TradingAccountsModal';
import MarketWatch from "../trading/MarketWatch";
import OrderBook from '../pages/OrderBook';
import History from '../pages/History';
import PositionReport from '../pages/Positionreport';
import { useDispatch, useSelector } from "react-redux";
import { getIndexScript } from "Store/Reducer/marketwatch.slice";
import { getAvailableBalance, getRangeList, hideMessage } from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import BannedBlockedScripts from '../pages/BannedBlockedScripts';
import ReportTrade from "views/pages/reports/ReportTrade";
import MarketWatchChart from "../../components/charts/MarketWatchChart";
import ChartToolRail from "../../components/charts/ChartToolRail";
import ChartHeaderControls from "../../components/charts/ChartHeaderControls";
import ReportLedger from "views/pages/reports/ReportLedger";
import ReportRejectionLog from "views/pages/reports/ReportRejectionLog";
import ReportUpdateLog from "views/pages/reports/ReportUpdateLog";
import TrialBalance from "views/pages/reports/TrialBalance";
import MaxQuantityDetails from '../pages/MaxQuantityDetails';
import WalletHubModal from '../../components/wallet/WalletHub/WalletHubModal';
import AccountMarginBar, { formatEquityPill } from "../../components/account/AccountMarginBar";
import {
  applyLivePnlToMarginSummary,
  sumLivePositionPnl,
} from "../../utils/marginSummaryLive";
import MarketWatchQuickOrderModal from '../../components/trading/MarketWatchQuickOrderModal';
import ChartSettingsModal from '../../components/charts/ChartSettingsModal';
import ColorTemplatesPage from '../../components/charts/ColorTemplatesPage';
import AboutProgramModal from '../../components/layout/AboutProgramModal';
import { pickIndexStripRows, indexRowLtp } from "utils/indexStripRows";
import { matchHotkey, shouldIgnoreGlobalHotkeys } from "utils/hotkeys";
import useViewport from "hooks/useViewport";
import { useBrokerTimeZoneId } from "../../utils/dateFormats";

/** Chart uses ResizeObserver; synthetic events cover flex-only resizes (watchlist drag, docks). */
function dispatchLayoutRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("resize"));
  window.dispatchEvent(new Event("fx-chart-layout"));
}

/** Min width where Symbol + Bid + Ask + LTP stay usable; drag cannot go narrower (use Hide instead). */
const WATCHLIST_PANEL_MIN_WIDTH = 358;
const WATCHLIST_PANEL_DEFAULT_WIDTH = 420;

/** Room the chart keeps when the position dock is dragged up. Volume overlays
 *  the price pane, so this only has to cover one pane plus the time axis. */
const CHART_MIN_HEIGHT = 220;
/** Usable table height once the dock is open. */
const POSITION_DOCK_MIN_HEIGHT = 120;
/** Thin grab strip when dock is collapsed — chart + marketwatch fill the rest. */
const POSITION_DOCK_COLLAPSED_HEIGHT = 10;
/** Release below this while dragging down → snap collapsed. */
const POSITION_DOCK_SNAP_COLLAPSE = 72;

const SHORTCUTS = [
  { section: "Global" },
  { key: "F1", desc: "Open shortcuts help" },
  { key: "F2", desc: "Open watchlist panel / return to chart page" },
  { key: "F3", desc: "Collapse / expand position panel" },
  { key: "Alt + T", desc: "Collapse / expand position panel" },
  { key: "Esc", desc: "Close open dialog / panel" },
  { section: "Trading" },
  { key: "F9", desc: "Open / close quick order for selected symbol" },
  { key: "Ctrl + N", desc: "Open / close quick order for selected symbol" },
  { key: "+ / =", desc: "Zoom in chart" },
  { key: "- / _", desc: "Zoom out chart" },
  { key: "← / →", desc: "Scroll chart left / right" },
  { key: "Mouse wheel", desc: "Scroll chart (Ctrl + wheel to zoom)" },
  { key: "Ctrl + S", desc: "Save chart as image (PNG)" },
];

const SIDEBAR_NAV = [
  { id:'history',   icon:'📜', label:'History',               page:'history'   },
  { id:'position',  icon:'📄', label:'Position Report',       page:'position'  },
  { id:'banned',    icon:'🚫', label:'Banned/Blocked Scripts', page:'banned'    },
  { id:'maxqty',    icon:'⚖️',  label:'Max Quantity Details',  page:'maxqty'    },
  {
    id:'reports', icon:'📊', label:'Reports',
    children:[
      { id:'trade-report',     label:'Trade Report',                page:'trade-report'      },
      { id:'ledger',           label:'Ledger Report',               page:'ledger'            },
      { id:'order-rejection',  label:'Order/Trade Rejection Log',   page:'order-rejection'   },
      { id:'order-editdelete', label:'Order/Trade Edit/Delete Log', page:'order-editdelete'  },
      { id:'trial-balance',    label:'Trial Balance',               page:'trial-balance'     },
    ],
  },
];

function getPageLabel(page) {
  if (page === "color-templates") return "Color Templates";
  for (const item of SIDEBAR_NAV) {
    if (item.page === page) return `${item.icon} ${item.label}`;
    if (item.children) for (const c of item.children) if (c.page === page) return `📊 ${c.label}`;
  }
  return '';
}

/** `only` narrows the strip to selected indices, so SENSEX can sit on the far
 *  right while the other two stay next to the chart controls. */
function HeaderIndexStrip({ scriptIndexList, only, className = "" }) {
  const { nifty, bankNifty, sensex } = useMemo(
    () => pickIndexStripRows(scriptIndexList),
    [scriptIndexList]
  );
  const items = [
    { label: "NIFTY 50", row: nifty },
    { label: "BNIFTY 50", row: bankNifty },
    { label: "SENSEX", row: sensex },
  ].filter(({ label }) => !only || only.includes(label));

  return (
    <div className={`top-index-strip${className ? ` ${className}` : ""}`}>
      {items.map(({ label, row }) => {
        const ltp = indexRowLtp(row);
        // Use pre-stored values from socket tick handler (same as MarketWatchIndexShow).
        // indexRowDisplayChange() recomputes from prevClose which is often 0/missing;
        // changeAmount / changePercent are set correctly by the tick handler.
        const delta = Number(row?.changeAmount ?? 0);
        const pctStr = row?.changePercent ?? "0.00";
        let chgClass = row?.changeAmountColor;
        if (!chgClass) {
          if (delta > 0) chgClass = "text-success";
          else if (delta < 0) chgClass = "text-danger";
          else chgClass = "";
        }
        const up = chgClass === "text-success";
        const down = chgClass === "text-danger";
        const color = up ? "#0f766e" : down ? "#b91c1c" : "#6b7280";
        const arrow = up ? "▲" : down ? "▼" : "•";
        const hasData = ltp > 0;
        return (
          <div key={label} className="top-index-item">
            <span className="idx-label">{label}</span>
            <span className="idx-values">
              <span className="idx-ltp">{hasData ? ltp.toFixed(2) : ""}</span>
              <span className="idx-chg" style={{ color }}>
                {hasData ? (
                  <>{arrow} {delta.toFixed(2)} ({pctStr}%)</>
                ) : null}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ThemeBridge({ darkTheme }) {
  useEffect(() => {
    const root = document.documentElement;
    if (darkTheme) {
      root.style.setProperty('--bg-primary',    '#1a0a0a');
      root.style.setProperty('--bg-secondary',  '#231212');
      root.style.setProperty('--border',        '#3a1a1a');
      root.style.setProperty('--text-primary',  '#e8d8c8');
      root.style.setProperty('--text-muted',    '#554444');
      root.style.setProperty('--green',         '#26a69a');
      root.style.setProperty('--red',           '#ef5350');
    } else {
      root.style.setProperty('--bg-primary',    '#f4f0ec');
      root.style.setProperty('--bg-secondary',  '#ede8e3');
      root.style.setProperty('--border',        '#c0b8ae');
      root.style.setProperty('--text-primary',  '#2a1a1a');
      root.style.setProperty('--text-muted',    '#9a8878');
      root.style.setProperty('--green',         '#1a7a70');
      root.style.setProperty('--red',           '#cc2820');
    }
  }, [darkTheme]);
  return null;
}

function PageRouter({ page, darkTheme, onColorTemplateApplied }) {
  if (page === 'history')   return <History darkTheme={darkTheme} />;
  if (page === 'tradebook') return <History darkTheme={darkTheme} />;
  if (page === 'orderbook') return <OrderBook darkTheme={darkTheme} />;
  if (page === 'position')  return <PositionReport darkTheme={darkTheme} />;
  if (page === 'banned')    return <BannedBlockedScripts darkTheme={darkTheme} />;
  if (page === 'maxqty')    return <MaxQuantityDetails />;
  if (page === 'trade-report')     return <ReportTrade darkTheme={darkTheme} />;
  if (page === 'ledger')           return <ReportLedger />;
  if (page === 'order-rejection')  return <ReportRejectionLog />;
  if (page === 'order-editdelete') return <ReportUpdateLog />;
  if (page === 'trial-balance')    return <TrialBalance />;
  if (page === 'color-templates') return <ColorTemplatesPage onApplied={onColorTemplateApplied} />;

  const dark = darkTheme;
  const MAP  = {
    'trade-report':     { icon:'📊', label:'Trade Report'                },
    ledger:             { icon:'📒', label:'Ledger Report'               },
    'order-rejection':  { icon:'❌', label:'Order/Trade Rejection Log'   },
    'order-editdelete': { icon:'✏️', label:'Order/Trade Edit/Delete Log' },
  };
  const { icon, label } = MAP[page] || { icon:'📄', label: page };
  return (
    <div style={{ padding:'24px', background: dark?'#1a0a0a':'#f4f0ec', minHeight:'100%', fontFamily: FONT_ROBOTO }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
        <span style={{ fontSize:16, color: dark?'#c8a87a':'#8a6030', fontWeight:700 }}>{icon} {label}</span>
      </div>
      <div style={{ background: dark?'#231212':'#ede8e3', border:`1px solid ${dark?'#3a1a1a':'#c0b8ae'}`, borderRadius:8, padding:'48px 32px', textAlign:'center', color: dark?'#664444':'#9a8878' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🚧</div>
        <div style={{ fontSize:14, fontWeight:600, color: dark?'#c8a87a':'#8a6030', marginBottom:8 }}>{label}</div>
        <div style={{ fontSize:12 }}>This module is under construction.</div>
      </div>
    </div>
  );
}

function ShortcutsModal({ onClose }) {
  return (
    <div className="modal-overlay shortcuts-modal-overlay" onClick={onClose}>
      <div className="modal-box shortcuts-modal-box" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h3>⌨️ Keyboard Shortcuts</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{paddingBottom:8}}>
          <div className="shortcuts-grid">
            {SHORTCUTS.map((s,i)=>
              s.section ? (
                <div key={i} className="shortcut-section-title">{s.section}</div>
              ) : (
                <div key={i} className="shortcut-row">
                  <span className="shortcut-key">{s.key}</span>
                  <span className="shortcut-desc">{s.desc}</span>
                </div>
              )
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-confirm" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return ()=>clearTimeout(t); }, [onDone]);
  return <div className="toast">{msg}</div>;
}

function Mt5Icon({ name }) {
  const common = { viewBox: "0 0 24 24", "aria-hidden": true };
  switch (name) {
    case "quotes":
      return (
        <svg {...common}>
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      );
    case "chart":
      return (
        <svg {...common}>
          <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" />
        </svg>
      );
    case "position":
      return (
        <svg {...common}>
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
          <path d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h6m-6 4h6" />
        </svg>
      );
    case "tradebook":
      return (
        <svg {...common}>
          <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
        </svg>
      );
    case "trade":
      return (
        <svg {...common}>
          <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
        </svg>
      );
    case "history":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    default:
      return null;
  }
}

function MobileBottomNav({ active, onQuotes, onChart, onTradeBook, onPositionReport, onSettings }) {
  const items = [
    { id: "quotes", label: "MarketWatch", onClick: onQuotes },
    { id: "chart", label: "Chart", onClick: onChart },
    { id: "tradebook", label: "History", onClick: onTradeBook },
    { id: "position", label: "Position Report", onClick: onPositionReport },
    { id: "settings", label: "Settings", onClick: onSettings },
  ];
  return (
    <nav className="fx-mobile-footer" aria-label="Mobile navigation">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`fx-mobile-footer__btn${active === item.id ? " fx-mobile-footer__btn--active" : ""}`}
          onClick={item.onClick}
        >
          <span className="fx-mobile-footer__icon">
            <Mt5Icon name={item.id} />
          </span>
          <span className="fx-mobile-footer__label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** Group digits for display (en-IN); preserves decimals; BigInt for huge integer parts. */
function formatLedgerBalanceNumber(rawBalance) {
  if (rawBalance === undefined || rawBalance === null) return null;
  const raw = String(rawBalance).trim().replace(/,/g, "");
  if (!raw) return null;
  const neg = raw.startsWith("-");
  const unsigned = neg ? raw.slice(1) : raw;
  const [intPart, frac] = unsigned.split(".");
  if (!intPart || !/^\d+$/.test(intPart)) return raw;
  try {
    const intFmt = BigInt(intPart).toLocaleString("en-IN");
    const body = frac !== undefined && frac !== "" ? `${intFmt}.${frac}` : intFmt;
    return neg ? `-${body}` : body;
  } catch {
    const n = Number(unsigned);
    if (!Number.isFinite(n)) return raw;
    return (neg ? "-" : "") + n.toLocaleString("en-IN", { maximumFractionDigits: 12 });
  }
}

function formatLedgerBalance(availableBalance) {
  if (!availableBalance || typeof availableBalance.balance === "undefined" || availableBalance.balance === null) {
    return null;
  }
  const num = formatLedgerBalanceNumber(availableBalance.balance);
  if (num == null) return null;
  const crDr = availableBalance.crDr ? ` ${availableBalance.crDr}` : "";
  return `${num}${crDr}`;
}

function ledgerBalanceColor(availableBalance) {
  if (availableBalance?.crDr === "Cr") return "var(--green)";
  if (availableBalance?.crDr === "Dr") return "var(--red)";
  return "var(--green)";
}

export default function TradingPlatform({ user, onLogout }) {
  useBrokerTimeZoneId();
  const dispatch = useDispatch();
  const { availableBalance, availableBalanceLoading } = useSelector((s) => s.common);
  const { marginSummary, marginSummaryLoading } = useSelector((s) => s.margin);
  const { positionReportList } = useSelector((s) => s.positionReport || {});
  const liveMarginSummary = useMemo(() => {
    const livePnl = sumLivePositionPnl(positionReportList);
    // Prefer live open-position MTM whenever we have rows (even if sum is 0).
    if (positionReportList?.length) {
      return applyLivePnlToMarginSummary(marginSummary, livePnl);
    }
    return marginSummary;
  }, [marginSummary, positionReportList]);

  const [menuOpen,            setMenuOpen]            = useState(false);
  const [symbolPanelOpen,     setSymbolPanelOpen]     = useState(false);
  const [darkTheme,           setDarkTheme]           = useState(false);
  const [showWalletHub,       setShowWalletHub]       = useState(false);
  const [showShortcuts,       setShowShortcuts]       = useState(false);
  const [showChartSettings,   setShowChartSettings]   = useState(false);
  const [showAboutProgram,    setShowAboutProgram]    = useState(false);
  const [showTradingAccounts, setShowTradingAccounts] = useState(false);
  const [toast,               setToast]               = useState(null);
  const { isMobile, isTablet, isCompact, width } = useViewport();
  const [connectedAccount,    setConnectedAccount]    = useState(null);
  const [activePage,          setActivePage]          = useState(null);
  const [reportsOpen,         setReportsOpen]         = useState(false);
  const [panelExpanded,       setPanelExpanded]       = useState(false);
  const [selectedChartRow, setSelectedChartRow] = useState(null);
  const [selectedChartSymbol, setSelectedChartSymbol] = useState("");
  const [quickOrderRow, setQuickOrderRow] = useState(null);
  const [symbolPanelWidth, setSymbolPanelWidth] = useState(WATCHLIST_PANEL_DEFAULT_WIDTH);
  const [watchlistDockHidden, setWatchlistDockHidden] = useState(false);
  const watchlistWidthBeforeHideRef = useRef(WATCHLIST_PANEL_DEFAULT_WIDTH);
  const [positionDockHeight, setPositionDockHeight] = useState(() => {
    if (typeof window === "undefined") return 200;
    return Math.min(260, Math.max(POSITION_DOCK_MIN_HEIGHT, Math.round(window.innerHeight * 0.26)));
  });
  const positionDockHeightBeforeCollapseRef = useRef(200);
  const dragLayoutRef = useRef({ kind: null, start: 0, startSize: 0, pointerId: null });
  const chartLayoutRafRef = useRef(null);
  const symbolWidthRafRef = useRef(null);
  const symbolWidthPendingRef = useRef(null);
  const lastSymbolRefreshAtRef = useRef(0);
  const layoutElRef = useRef(null);
  const prevMenuOpenRef = useRef(false);

  useEffect(() => {
    // Cache main layout element for fast CSS var updates during drag (avoid querySelector per mousemove).
    if (typeof document === "undefined") return;
    layoutElRef.current = document.querySelector(".main-layout");
  }, []);

  const getSymbolPanelMaxWidth = useCallback(() => {
    const layoutEl = typeof document !== "undefined" ? document.querySelector(".main-layout") : null;
    const layoutWidth = layoutEl?.clientWidth || (typeof window !== "undefined" ? window.innerWidth : 1200);
    const resizeBarWidth = 6;

    // Responsive clamp: keep chart usable on all desktop widths.
    // - Max panel width is capped by a % of the available layout width.
    // - Also enforce a minimum chart width (scales with screen).
    const maxRatio =
      layoutWidth >= 1600 ? 0.36 :
      layoutWidth >= 1200 ? 0.42 :
      layoutWidth >= 992  ? 0.48 :
                            0.55;
    const maxByRatio = Math.floor(layoutWidth * maxRatio);

    const minChartWidth = Math.max(360, Math.floor(layoutWidth * 0.45));
    const maxByChartMin = Math.floor(layoutWidth - minChartWidth - resizeBarWidth);

    return Math.max(240, Math.min(maxByRatio, maxByChartMin));
  }, []);

  /** Keep docked order form in sync when user picks another symbol while the panel is open. */
  useEffect(() => {
    setQuickOrderRow((prev) => {
      if (!prev) return prev;
      if (selectedChartRow && typeof selectedChartRow === "object") return selectedChartRow;
      return prev;
    });
  }, [selectedChartRow]);

  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);

  const activePageRef = useRef(null);

  const themeKey = darkTheme ? 'dark' : 'light';

  const displayName = connectedAccount?.name  || connectedAccount?.login
                   || user?.UserName || user?.userName || user?.name || user?.email || 'User';
  const displayCode = connectedAccount?.login || connectedAccount?.userCode
                   || user?.UserCode || user?.userCode || user?.id || '';

  /** Reflow chart and flex panes when viewport breakpoint or width changes. */
  useEffect(() => {
    const id = requestAnimationFrame(() => dispatchLayoutRefresh());
    return () => cancelAnimationFrame(id);
  }, [isMobile, isTablet, isCompact, width]);

  /**
   * Clamp the position dock. Dragging down can shrink to a thin grab strip so
   * chart + marketwatch fill the height; dragging up restores a usable table.
   */
  const clampDockHeight = useCallback((raw) => {
    const areaH =
      layoutElRef.current?.clientHeight ||
      (typeof window !== "undefined" ? window.innerHeight : 800);
    const max = Math.max(
      POSITION_DOCK_MIN_HEIGHT,
      Math.min(Math.floor(areaH * 0.40), areaH - CHART_MIN_HEIGHT)
    );
    return Math.min(max, Math.max(POSITION_DOCK_COLLAPSED_HEIGHT, Math.round(raw)));
  }, []);

  const isPositionDockCollapsed = positionDockHeight <= POSITION_DOCK_SNAP_COLLAPSE;

  const togglePositionDockCollapsed = useCallback(() => {
    setPositionDockHeight((prev) => {
      if (prev <= POSITION_DOCK_SNAP_COLLAPSE) {
        const restore = positionDockHeightBeforeCollapseRef.current || 200;
        return clampDockHeight(Math.max(POSITION_DOCK_MIN_HEIGHT, restore));
      }
      positionDockHeightBeforeCollapseRef.current = prev;
      return POSITION_DOCK_COLLAPSED_HEIGHT;
    });
  }, [clampDockHeight]);

  /** Synthetic layout refresh when position dock height toggles. */
  useEffect(() => {
    if (activePage) return;
    const id = requestAnimationFrame(() => dispatchLayoutRefresh());
    return () => cancelAnimationFrame(id);
  }, [activePage, positionDockHeight]);

  /** Keep the position dock from eating the chart on short viewports. */
  useEffect(() => {
    const onResize = () =>
      setPositionDockHeight((prev) => {
        if (prev <= POSITION_DOCK_SNAP_COLLAPSE) return POSITION_DOCK_COLLAPSED_HEIGHT;
        return clampDockHeight(prev);
      });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampDockHeight]);

  // ── Only dispatch getIndexScript here — MarketWatch.js handles getMarketWatchList
  useEffect(() => {
    dispatch(getIndexScript());
    // NOTE: getMarketWatchList is intentionally NOT called here.
    // MarketWatch.js calls it on its own mount. Calling it here too
    // caused a race condition + duplicate API calls.
  }, [dispatch]);

  useEffect(() => {
    if (user?.userId) {
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
      dispatch(getRangeList());
    }
  }, [dispatch, user?.userId]);

  // Slow poll for Balance/Credit/Used (§11.1); P&L/Equity refresh with positions elsewhere.
  useEffect(() => {
    if (!user?.userId) return undefined;
    const id = setInterval(() => {
      dispatch(getMarginSummary());
    }, 15000);
    return () => clearInterval(id);
  }, [dispatch, user?.userId]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, []);

  const navigate = useCallback((page) => {
    dispatch(hideMessage());
    setMenuOpen(false);
    requestAnimationFrame(() => dispatchLayoutRefresh());
    setActivePage(page);
    activePageRef.current = page;
  }, [dispatch]);

  const goBackToSettings = useCallback(() => {
    dispatch(hideMessage());
    setActivePage(null);
    activePageRef.current = null;
    setSymbolPanelOpen(false);
    setMenuOpen(true);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [dispatch]);

  const goHome = useCallback(() => {
    dispatch(hideMessage());
    setActivePage(null);
    activePageRef.current = null;
    setSymbolPanelOpen(false);
    closeMenu();
  }, [dispatch, closeMenu]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (quickOrderRow)       { setQuickOrderRow(null);        return; }
      if (showWalletHub)       { setShowWalletHub(false);       return; }
      if (showShortcuts)       { setShowShortcuts(false);       return; }
      if (showChartSettings)   { setShowChartSettings(false);   return; }
      if (activePage === 'color-templates') { goBackToSettings(); return; }
      if (showAboutProgram)    { setShowAboutProgram(false);    return; }
      if (showTradingAccounts) { setShowTradingAccounts(false); return; }
      if (menuOpen) {
        setMenuOpen(false);
        requestAnimationFrame(() => dispatchLayoutRefresh());
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quickOrderRow, showWalletHub, showShortcuts, showChartSettings, showAboutProgram, showTradingAccounts, menuOpen, activePage, goBackToSettings]);

  const handleAccountSwitch = useCallback((account) => {
    setConnectedAccount(account);
    setToast(`✅ Switched to ${account.name||account.login} (${account.server})`);
    dispatch(getAvailableBalance());
    dispatch(getMarginSummary());
  }, [dispatch]);

  const toggleTheme = () => setDarkTheme(d=>!d);

  useEffect(() => {
    if (activePage) setQuickOrderRow(null);
  }, [activePage]);

  const ledgerLabel = formatLedgerBalance(availableBalance);
  const ledgerColor = ledgerBalanceColor(availableBalance);
  const equityLabel = formatEquityPill(liveMarginSummary) || ledgerLabel;
  const walletLoading = availableBalanceLoading || marginSummaryLoading;

  /** Same as old web: view-password login sets `isMasterRole: false` on the session. */
  const viewOnlySession = user?.isMasterRole === false;

  const mobileFooterActive = useMemo(() => {
    if (menuOpen) return "settings";
    if (activePage === "color-templates") return "settings";
    if (activePage === "position") return "position";
    if (activePage === "tradebook" || activePage === "history") return "tradebook";
    if (symbolPanelOpen && !activePage) return "quotes";
    if (!activePage) return "chart";
    return "chart";
  }, [menuOpen, symbolPanelOpen, activePage]);

  const openMobileWatchlist = useCallback(() => {
    closeMenu();
    goHome();
    setPanelExpanded(false);
    setSymbolPanelOpen(true);
  }, [closeMenu, goHome]);

  const openMobileChart = useCallback(() => {
    closeMenu();
    goHome();
    setSymbolPanelOpen(false);
  }, [closeMenu, goHome]);

  const openMobileTradeBook = useCallback(() => {
    closeMenu();
    setSymbolPanelOpen(false);
    navigate("history");
  }, [closeMenu, navigate]);

  const openMobilePositionReport = useCallback(() => {
    closeMenu();
    setSymbolPanelOpen(false);
    navigate("position");
  }, [closeMenu, navigate]);

  const openMobileSettings = useCallback(() => {
    setSymbolPanelOpen(false);
    setMenuOpen(true);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  // Keep side effects out of state-updater/render path.
  useEffect(() => {
    const wasOpen = prevMenuOpenRef.current;
    if (!wasOpen && menuOpen) {
      dispatch(hideMessage());
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
    } else if (wasOpen && !menuOpen) {
      requestAnimationFrame(() => dispatchLayoutRefresh());
    }
    prevMenuOpenRef.current = menuOpen;
  }, [menuOpen, dispatch]);

  const onLayoutDragMove = useCallback((e) => {
    const d = dragLayoutRef.current;
    if (!d.kind) return;
    if (d.pointerId != null && e.pointerId != null && d.pointerId !== e.pointerId) return;
    if (d.kind === "symbol") {
      const dx = d.start - e.clientX;
      const maxAllowed = getSymbolPanelMaxWidth();
      const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
      const next = Math.min(maxAllowed, Math.max(minVisible, d.startSize + dx));
      symbolWidthPendingRef.current = next;
      // Immediate visual feedback: update CSS var directly (smooth cursor tracking).
      // React state update is still batched in rAF below for performance.
      const el = layoutElRef.current;
      if (el && !el.classList.contains("panel-expanded")) {
        // Only applies on the chart page (same condition as render uses).
        el.style.setProperty("--fx-symbol-panel-width", `${next}px`);
      }
      if (symbolWidthRafRef.current == null) {
        symbolWidthRafRef.current = requestAnimationFrame(() => {
          symbolWidthRafRef.current = null;
          const pending = symbolWidthPendingRef.current;
          symbolWidthPendingRef.current = null;
          if (typeof pending === "number") setSymbolPanelWidth(pending);
          // Synthetic chart refresh is expensive; throttle while dragging.
          const now = Date.now();
          if (now - lastSymbolRefreshAtRef.current > 90) {
            lastSymbolRefreshAtRef.current = now;
            dispatchLayoutRefresh();
          }
        });
      }
    } else if (d.kind === "position") {
      const dy = d.start - e.clientY;
      const next = clampDockHeight(d.startSize + dy);
      setPositionDockHeight(next);
    }
  }, [getSymbolPanelMaxWidth, clampDockHeight]);

  const endLayoutDrag = useCallback((e) => {
    const d = dragLayoutRef.current;
    // Ignore unrelated pointer up/cancel when a different pointer started the drag.
    if (d.pointerId != null && e?.pointerId != null && d.pointerId !== e.pointerId) return;
    const endedKind = d.kind;
    dragLayoutRef.current = { kind: null, start: 0, startSize: 0, pointerId: null };
    const pendingWidth = symbolWidthPendingRef.current;
    if (typeof pendingWidth === "number") {
      setSymbolPanelWidth(pendingWidth);
      symbolWidthPendingRef.current = null;
    }
    // Clear imperative style override; render state will own it.
    const el = layoutElRef.current;
    if (el) el.style.removeProperty("--fx-symbol-panel-width");
    if (symbolWidthRafRef.current != null) {
      cancelAnimationFrame(symbolWidthRafRef.current);
      symbolWidthRafRef.current = null;
    }
    if (chartLayoutRafRef.current != null) {
      cancelAnimationFrame(chartLayoutRafRef.current);
      chartLayoutRafRef.current = null;
    }
    window.removeEventListener("pointermove", onLayoutDragMove);
    window.removeEventListener("pointerup", endLayoutDrag);
    window.removeEventListener("pointercancel", endLayoutDrag);

    // Drag position handle down far enough → collapse to thin strip (full chart + MW).
    if (endedKind === "position") {
      setPositionDockHeight((prev) => {
        if (prev < POSITION_DOCK_SNAP_COLLAPSE) {
          positionDockHeightBeforeCollapseRef.current =
            d.startSize > POSITION_DOCK_SNAP_COLLAPSE ? d.startSize : positionDockHeightBeforeCollapseRef.current;
          return POSITION_DOCK_COLLAPSED_HEIGHT;
        }
        if (prev < POSITION_DOCK_MIN_HEIGHT) {
          return clampDockHeight(POSITION_DOCK_MIN_HEIGHT);
        }
        return prev;
      });
    }

    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [onLayoutDragMove, clampDockHeight]);

  const startSymbolResize = useCallback((e) => {
    e.preventDefault();
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    dragLayoutRef.current = { kind: "symbol", start: e.clientX, startSize: symbolPanelWidth, pointerId: e.pointerId };
    symbolWidthPendingRef.current = null;
    lastSymbolRefreshAtRef.current = 0;
    window.addEventListener("pointermove", onLayoutDragMove);
    window.addEventListener("pointerup", endLayoutDrag);
    window.addEventListener("pointercancel", endLayoutDrag);
  }, [symbolPanelWidth, onLayoutDragMove, endLayoutDrag]);

  const startPositionResize = useCallback((e) => {
    e.preventDefault();
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    dragLayoutRef.current = { kind: "position", start: e.clientY, startSize: positionDockHeight, pointerId: e.pointerId };
    window.addEventListener("pointermove", onLayoutDragMove);
    window.addEventListener("pointerup", endLayoutDrag);
    window.addEventListener("pointercancel", endLayoutDrag);
  }, [positionDockHeight, onLayoutDragMove, endLayoutDrag]);

  useEffect(() => {
    const clampNow = () => {
      if (watchlistDockHidden) return;
      const maxAllowed = getSymbolPanelMaxWidth();
      const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
      setSymbolPanelWidth((prev) => Math.min(maxAllowed, Math.max(minVisible, prev)));
    };
    clampNow();
    window.addEventListener("resize", clampNow);
    return () => window.removeEventListener("resize", clampNow);
  }, [menuOpen, panelExpanded, getSymbolPanelMaxWidth, watchlistDockHidden]);

  const hideWatchlistPanel = useCallback(() => {
    if (isMobile) return;
    watchlistWidthBeforeHideRef.current = symbolPanelWidth;
    setWatchlistDockHidden(true);
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [isMobile, symbolPanelWidth]);

  const showWatchlistPanel = useCallback(() => {
    if (isMobile) return;
    setWatchlistDockHidden(false);
    setSymbolPanelWidth(() => {
      const saved = watchlistWidthBeforeHideRef.current;
      const maxAllowed = getSymbolPanelMaxWidth();
      const minVisible = Math.min(WATCHLIST_PANEL_MIN_WIDTH, maxAllowed);
      const w =
        saved >= WATCHLIST_PANEL_MIN_WIDTH ? saved : WATCHLIST_PANEL_DEFAULT_WIDTH;
      return Math.min(maxAllowed, Math.max(minVisible, w));
    });
    requestAnimationFrame(() => dispatchLayoutRefresh());
  }, [getSymbolPanelMaxWidth, isMobile]);

  const openQuickOrderForRow = useCallback(
    (row) => {
      if (viewOnlySession || !row || typeof row !== "object") return;
      setQuickOrderRow(row);
    },
    [viewOnlySession]
  );

  const openQuickOrderFromShortcut = useCallback(() => {
    if (viewOnlySession) {
      setToast("View-only login cannot place orders.");
      return;
    }
    if (quickOrderRow) {
      setQuickOrderRow(null);
      return;
    }
    if (selectedChartRow && typeof selectedChartRow === "object") {
      setQuickOrderRow(selectedChartRow);
      return;
    }
    setToast("Select a symbol in the watchlist first.");
  }, [viewOnlySession, quickOrderRow, selectedChartRow]);

  useEffect(() => {
    const onShortcutKey = (e) => {
      if (shouldIgnoreGlobalHotkeys(e)) return;

      // Keep Escape close behavior centralized.
      if (e.key === "Escape") return;

      if (matchHotkey(e, "f1")) {
        e.preventDefault();
        if (!isMobile) setShowShortcuts((v) => !v);
        return;
      }
      if (matchHotkey(e, "f2")) {
        e.preventDefault();
        if (activePage) {
          goHome();
          return;
        }
        if (isMobile) {
          setSymbolPanelOpen((v) => !v);
          return;
        }
        if (watchlistDockHidden) {
          showWatchlistPanel();
        } else {
          hideWatchlistPanel();
        }
        return;
      }
      if (matchHotkey(e, "f9") || matchHotkey(e, "ctrl+n")) {
        e.preventDefault();
        openQuickOrderFromShortcut();
        return;
      }
      if (matchHotkey(e, "f3") || matchHotkey(e, "alt+t")) {
        e.preventDefault();
        togglePositionDockCollapsed();
      }
    };

    window.addEventListener("keydown", onShortcutKey);
    return () => window.removeEventListener("keydown", onShortcutKey);
  }, [
    activePage,
    goHome,
    isMobile,
    watchlistDockHidden,
    showWatchlistPanel,
    hideWatchlistPanel,
    openQuickOrderFromShortcut,
    togglePositionDockCollapsed,
  ]);

  const mobileChartFocus = isMobile && !activePage && !symbolPanelOpen && !menuOpen;

  return (
    <div
      className={`trading-platform ${themeKey}${menuOpen ? " trading-platform--menu-open" : ""}${isMobile ? " trading-platform--mobile" : ""}${isTablet ? " trading-platform--tablet" : ""}${isCompact ? " trading-platform--compact" : ""}${mobileChartFocus ? " trading-platform--mobile-chart-focus" : ""}${isMobile && symbolPanelOpen && !activePage ? " trading-platform--watchlist-open" : ""}${isMobile && quickOrderRow && !activePage ? " trading-platform--order-open" : ""}`}
      style={{
        // Side menu + backdrop start below header; extra offset when view-only banner is shown
        ["--fx-menu-top"]: viewOnlySession ? "84px" : "52px",
        ...((!isMobile || activePage) ? { ["--fx-mobile-indices-h"]: "0px" } : {}),
      }}
    >

      <ThemeBridge darkTheme={darkTheme} />

      {/* ── TOP BAR ── */}
      <header className="top-bar">
        <div className="top-bar__left">
          <button
            className="icon-btn menu-btn"
            onClick={toggleMenu}
            title="Menu"
          >☰</button>

          {activePage && !isMobile && (
            <button onClick={goHome} style={{background:'transparent',border:'1px solid var(--border)',color:'var(--green)',padding:'3px 10px',borderRadius:3,cursor:'pointer',fontSize:11,fontWeight:700,marginRight:4}}>
              ← Chart
            </button>
          )}
          <div className="account-info">
            <span className="account-name">{displayName}</span>
            <span className="account-id">{displayCode}</span>
          </div>

          {/* Chart interval / Indicators strip, hoisted out of the chart iframe */}
          {!activePage && !isMobile && <ChartHeaderControls />}
        </div>

        <div className="top-bar__center top-bar__center--with-new-order">
          {activePage && !isMobile ? (
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--green)" }}>{getPageLabel(activePage)}</span>
          ) : !activePage ? (
            <>
              <button
                className={`top-bar-mt5-new-order${quickOrderRow ? " top-bar-mt5-new-order--active top-bar-mt5-new-order--close-only" : ""}`}
                type="button"
                disabled={viewOnlySession}
                title={
                  viewOnlySession
                    ? "View-only login — sign in with trading password to trade"
                    : quickOrderRow
                      ? "Close order panel"
                      : "New order (selected watchlist symbol)"
                }
                onClick={() => {
                  if (viewOnlySession) return;
                  if (quickOrderRow) {
                    setQuickOrderRow(null);
                    return;
                  }
                  if (selectedChartRow && typeof selectedChartRow === "object") {
                    setQuickOrderRow(selectedChartRow);
                  } else {
                    setToast("Select a symbol in the watchlist first.");
                  }
                }}
              >
                {quickOrderRow ? (
                  <span className="top-bar-mt5-new-order__close-x" aria-hidden>✕</span>
                ) : (
                  <>
                    <svg className="top-bar-mt5-new-order__clock" viewBox="0 0 24 24" width="15" height="15" aria-hidden>
                      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4l3 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {!isMobile && <span className="top-bar-mt5-new-order__text">New Order</span>}
                  </>
                )}
              </button>
              {!isMobile && <HeaderIndexStrip scriptIndexList={scriptIndexList} />}
            </>
          ) : null}
        </div>

        <div className="top-bar__wallet-slot">
          {(!isMobile || (!activePage && !menuOpen)) && (
          <button
            type="button"
            className={`wallet-pill wallet-pill--featured wallet-pill--button wallet-pill--icon-only${walletLoading ? " wallet-pill--loading" : ""}`}
            title={
              liveMarginSummary
                ? `Equity ${equityLabel} · Free ${Number(liveMarginSummary.freeMargin).toFixed(2)} · Level ${
                    liveMarginSummary.marginLevel == null ? "—" : `${Number(liveMarginSummary.marginLevel).toFixed(1)}%`
                  }`
                : "Wallet — balance, equity, margin"
            }
            disabled={!user?.userId}
            onClick={() => user?.userId && setShowWalletHub(true)}
            aria-label="Wallet"
          >
            {/* Icon only — the equity figure is already in the bottom status bar */}
            <span className="wallet-pill__icon" aria-hidden>👛</span>
          </button>
          )}
        </div>

        <div className="top-bar__right top-bar__right--actions">
          <button className="icon-btn theme-btn" onClick={toggleTheme}>{darkTheme?'☀️':'🌙'}</button>
          {!isMobile && (
            <button className="icon-btn" onClick={()=>setSymbolPanelOpen(o=>!o)}>📋</button>
          )}
        </div>
      </header>

      {isMobile && !activePage && !menuOpen && (
        <div className="top-bar-mobile-indices">
          <HeaderIndexStrip scriptIndexList={scriptIndexList} />
        </div>
      )}

      {viewOnlySession && (
        <div
          className="view-only-banner"
          style={{
            padding: "8px 12px",
            textAlign: "center",
            fontSize: 12,
            fontWeight: 600,
            background: "var(--bg-secondary)",
            color: "var(--text-muted)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          View only — you signed in with a <strong>view password</strong>. Charts and reports work; use your{" "}
          <strong>trading password</strong> to place orders.
        </div>
      )}

      {menuOpen && (
        <>
          {/* Below .top-bar (see z-index in SCSS); dims chart/watchlist; does not cover header */}
          <div
            className="side-menu-backdrop"
            role="presentation"
            aria-hidden
            onClick={closeMenu}
          />
          <nav className={`side-menu${isMobile ? " side-menu--mt5" : ""}`} aria-label="Main menu">
            {isMobile && (
              <div className="fx-mt5-settings-hero">
                <h1 className="fx-mt5-settings-hero__title">Settings</h1>
                <div className="fx-mt5-settings-hero__name">{displayName}</div>
                <div className="fx-mt5-settings-hero__meta">{displayCode}</div>
              </div>
            )}
            <ul className="menu-list">

              {isMobile && (
                <>
                  <li className="menu-item menu-item--mobile-quick" onClick={() => { navigate("position"); closeMenu(); }}>
                    <span className="menu-icon">📊</span>
                    <span className="menu-label">Position Report</span>
                    <span className="menu-arrow">›</span>
                  </li>
                  <li className="menu-item menu-item--mobile-quick" onClick={() => { navigate("history"); closeMenu(); }}>
                    <span className="menu-icon">📝</span>
                    <span className="menu-label">History</span>
                    <span className="menu-arrow">›</span>
                  </li>
                </>
              )}

              <li className="menu-item menu-item--section-gap" onClick={()=>{ setShowTradingAccounts(true); closeMenu(); }}>
                <span className="menu-icon">👤</span>
                <span className="menu-label">Trading Accounts</span>
                <span className="menu-arrow">›</span>
              </li>

              <li
                className="menu-item"
                style={{ opacity: user?.userId ? 1 : 0.45 }}
                onClick={() => {
                  if (!user?.userId) return;
                  setShowWalletHub(true);
                  closeMenu();
                }}
              >
                <span className="menu-icon">👛</span>
                <span className="menu-label">Wallet</span>
                <span className="menu-arrow">›</span>
              </li>

              <li className="menu-item" onClick={toggleTheme}>
                <span className="menu-icon">{darkTheme?'☀️':'🌙'}</span>
                <span className="menu-label">{darkTheme?'Light Theme':'Dark Theme'}</span>
              </li>

              <li
                className="menu-item"
                onClick={() => { setShowChartSettings(true); closeMenu(); }}
              >
                <span className="menu-icon">📊</span>
                <span className="menu-label">Chart Settings</span>
                <span className="menu-arrow">›</span>
              </li>

              <li
                className="menu-item"
                onClick={() => { navigate("color-templates"); }}
              >
                <span className="menu-icon">🎨</span>
                <span className="menu-label">Color Templates</span>
              </li>

              {SIDEBAR_NAV.filter((item) => !(isMobile && (item.page === "position" || item.page === "orderbook"))).map(item =>
                item.children ? (
                  <React.Fragment key={item.id}>
                    <li
                      className="menu-item"
                      style={{ justifyContent:'space-between' }}
                      onClick={() => setReportsOpen(o => !o)}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span className="menu-icon">{item.icon}</span>
                        <span className="menu-label">{item.label}</span>
                      </div>
                      <span className="menu-arrow" style={{ transition:'transform .2s', transform:reportsOpen?'rotate(90deg)':'none' }}>›</span>
                    </li>
                    {reportsOpen && item.children.map(child => (
                      <li
                        key={child.id}
                        className="menu-item"
                        style={{ paddingLeft:32, background:activePage===child.page?'var(--bg-primary)':'' }}
                        onClick={() => navigate(child.page)}
                      >
                        <span style={{ color:'var(--green)', fontSize:9, marginRight:6 }}>▸</span>
                        <span className="menu-label" style={{ color:activePage===child.page?'var(--green)':'' }}>{child.label}</span>
                      </li>
                    ))}
                  </React.Fragment>
                ) : (
                  <li
                    key={item.id}
                    className="menu-item"
                    style={{ background:activePage===item.page?'var(--bg-primary)':'' }}
                    onClick={() => navigate(item.page)}
                  >
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label" style={{ color:activePage===item.page?'var(--green)':'' }}>{item.label}</span>
                    {activePage===item.page && <span style={{ marginLeft:'auto', color:'var(--green)', fontSize:9 }}>●</span>}
                  </li>
                )
              )}

              {!isMobile && (
              <li className="menu-item" onClick={()=>{ setShowShortcuts(true); closeMenu(); }}>
                <span className="menu-icon">⌨️</span>
                <span className="menu-label">Shortcuts</span>
              </li>
              )}

              <li
                className="menu-item"
                onClick={() => { setShowAboutProgram(true); closeMenu(); }}
              >
                <span className="menu-icon">ℹ️</span>
                <span className="menu-label">About Program</span>
              </li>

            </ul>

            <div className="menu-logout" onClick={onLogout}>
              <span className="menu-icon">🚪</span><span>Logout</span>
            </div>
          </nav>
        </>
      )}

      {/* ── MAIN LAYOUT ── */}
      <div
        className={`main-layout ${panelExpanded ? 'panel-expanded' : ''}`}
        style={{
          ["--fx-symbol-panel-width"]:
            !activePage && (isMobile || !watchlistDockHidden) ? `${symbolPanelWidth}px` : undefined,
        }}
      >
        {activePage ? (
          <div
            className={`fx-page-scroll${isMobile && activePage !== "color-templates" ? " fx-mobile-page-scroll" : ""}${isMobile && activePage && activePage !== "color-templates" ? " fx-mobile-book-page" : ""}${activePage === "color-templates" ? " fx-mobile-color-templates-page" : ""}`}
            style={
              activePage === "color-templates"
                ? { flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }
                : { flex: 1, overflowY: "auto", minWidth: 0 }
            }
          >
            {isMobile && (
              <div
                className={`fx-mobile-page-header${activePage === "color-templates" ? " fx-mobile-page-header--mt5-settings" : ""}`}
              >
                <button
                  type="button"
                  className="fx-mobile-page-header__back"
                  onClick={activePage === "color-templates" ? goBackToSettings : goHome}
                  aria-label={activePage === "color-templates" ? "Back to settings" : "Back to chart"}
                >
                  {activePage === "color-templates" ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M14.5 6.5L9 12l5.5 5.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    "← Chart"
                  )}
                </button>
                <h1 className="fx-mobile-page-header__title">{getPageLabel(activePage)}</h1>
                {activePage === "color-templates" ? (
                  <span className="fx-mobile-page-header__spacer" aria-hidden />
                ) : null}
              </div>
            )}
            <PageRouter
              page={activePage}
              darkTheme={darkTheme}
              onColorTemplateApplied={(label) => setToast(`Color template: ${label}`)}
            />
          </div>
        ) : (
          <div className="fx-chart-area">
            {!isMobile && <ChartToolRail />}
            {!isMobile && !activePage && watchlistDockHidden && (
              <button
                type="button"
                className="fx-watchlist-edge-restore"
                title="Show watchlist panel"
                aria-label="Show watchlist panel"
                onClick={showWatchlistPanel}
              >
                <span className="fx-watchlist-edge-restore__label">MarketWatch</span>
              </button>
            )}
            <div className="fx-chart-area__stack">
            <main className="fx-chart-area__main">
              <section className="fx-chart-pane">
                {!activePage && quickOrderRow && (
                  <MarketWatchQuickOrderModal
                    docked={!isMobile}
                    isMobile={isMobile}
                    darkTheme={darkTheme}
                    open
                    row={quickOrderRow}
                    menuOpen={menuOpen}
                    onClose={() => setQuickOrderRow(null)}
                  />
                )}
                <MarketWatchChart
                  symbol={selectedChartSymbol}
                  selectedRow={selectedChartRow}
                  darkMode={darkTheme}
                  menuOpen={menuOpen}
                  viewOnly={viewOnlySession}
                  orderPanelOpen={!!quickOrderRow}
                  onOpenOrder={(row) => {
                    if (viewOnlySession) return;
                    if (quickOrderRow) {
                      setQuickOrderRow(null);
                      return;
                    }
                    const target =
                      row && typeof row === "object"
                        ? row
                        : selectedChartRow && typeof selectedChartRow === "object"
                          ? selectedChartRow
                          : null;
                    if (target) setQuickOrderRow(target);
                    else setToast("Select a symbol in the watchlist first.");
                  }}
                />
              </section>
              {isMobile ? (
                <>
                  {symbolPanelOpen && (
                    <button
                      type="button"
                      className="symbol-panel-backdrop"
                      aria-label="Close watchlist"
                      onClick={() => setSymbolPanelOpen(false)}
                    />
                  )}
                  <aside className={`symbol-panel symbol-panel--mobile-full ${symbolPanelOpen ? "open" : ""}`}>
                    <div className="fx-mobile-watchlist-header">
                      <h2 className="fx-mobile-watchlist-header__title">MarketWatch</h2>
                      <button
                        type="button"
                        className="fx-mobile-watchlist-header__close"
                        onClick={() => setSymbolPanelOpen(false)}
                        aria-label="Close watchlist"
                      >
                        ✕
                      </button>
                    </div>
                    <MarketWatch
                      setPanelExpanded={setPanelExpanded}
                      darkTheme={darkTheme}
                      selectedChartSymbol={selectedChartSymbol}
                      viewOnly={viewOnlySession}
                      mobileFullscreen
                      watchlistMinWidthPx={400}
                      onSymbolSelectWithRow={(symbol, row) => {
                        setSelectedChartRow(row ?? null);
                        const key = String(
                          symbol || row?.script || row?.displayName || row?.symbol || ""
                        ).trim();
                        setSelectedChartSymbol(key);
                        setSymbolPanelOpen(false);
                      }}
                      onOpenQuickOrder={openQuickOrderForRow}
                      onBidAskOpenOrder={(row) => openQuickOrderForRow(row)}
                    />
                  </aside>
                </>
              ) : (
                !watchlistDockHidden && (
                  <>
                    <div
                      className="fx-resize-bar fx-resize-bar--vertical"
                      role="separator"
                      aria-label="Resize watchlist width"
                      onPointerDown={startSymbolResize}
                    />
                    <aside className="symbol-panel">
                      <MarketWatch
                        setPanelExpanded={setPanelExpanded}
                        darkTheme={darkTheme}
                        selectedChartSymbol={selectedChartSymbol}
                        viewOnly={viewOnlySession}
                        watchlistMinWidthPx={WATCHLIST_PANEL_MIN_WIDTH}
                        onRequestHideWatchlistPanel={hideWatchlistPanel}
                        onSymbolSelectWithRow={(symbol, row) => {
                          setSelectedChartRow(row ?? null);
                          const key = String(
                            symbol || row?.script || row?.displayName || row?.symbol || ""
                          ).trim();
                          setSelectedChartSymbol(key);
                        }}
                        onOpenQuickOrder={openQuickOrderForRow}
                        onBidAskOpenOrder={(row) => openQuickOrderForRow(row)}
                      />
                    </aside>
                  </>
                )
              )}
            </main>
            {!isMobile && (
              <div
                className={`fx-position-sheet${isPositionDockCollapsed ? " fx-position-sheet--collapsed" : ""}`}
                style={{ height: positionDockHeight }}
              >
                <div
                  className="fx-position-sheet__resize"
                  role="separator"
                  aria-label={
                    isPositionDockCollapsed
                      ? "Drag up to show position panel"
                      : "Resize position panel height — drag down to hide"
                  }
                  title={
                    isPositionDockCollapsed
                      ? "Drag up to show positions"
                      : "Drag down to show full chart + market watch"
                  }
                  onPointerDown={startPositionResize}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    togglePositionDockCollapsed();
                  }}
                />
                {!isPositionDockCollapsed && (
                  <div
                    className="fx-position-sheet__body"
                    style={{ paddingLeft: menuOpen ? 0 : 6 }}
                  >
                    <PositionReport embedded darkTheme={darkTheme} />
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {user?.userId && !menuOpen && !activePage && (
        <footer className="account-margin-footer">
          <AccountMarginBar
            variant="footer"
            summary={liveMarginSummary}
            loading={marginSummaryLoading}
            onRefresh={() => dispatch(getMarginSummary())}
          />
        </footer>
      )}

      {isMobile && (
        <MobileBottomNav
          active={mobileFooterActive}
          onQuotes={openMobileWatchlist}
          onChart={openMobileChart}
          onTradeBook={openMobileTradeBook}
          onPositionReport={openMobilePositionReport}
          onSettings={openMobileSettings}
        />
      )}

      {showWalletHub && user?.userId && (
        <WalletHubModal
          user={user}
          displayName={displayName}
          ledgerLabel={ledgerLabel}
          ledgerColor={ledgerColor}
          marginSummary={liveMarginSummary}
          marginSummaryLoading={marginSummaryLoading}
          darkTheme={darkTheme}
          onClose={() => setShowWalletHub(false)}
          onAddSuccess={() => {
            setToast("Pay-in request submitted.");
            dispatch(getAvailableBalance());
            dispatch(getMarginSummary());
          }}
          onRefreshBalance={() => {
            dispatch(getAvailableBalance());
            dispatch(getMarginSummary());
          }}
        />
      )}
      {showShortcuts && (
        <ShortcutsModal onClose={()=>setShowShortcuts(false)}/>
      )}
      {showChartSettings && (
        <ChartSettingsModal
          darkTheme={darkTheme}
          onClose={() => setShowChartSettings(false)}
          onSaved={() => setToast("Chart settings applied.")}
        />
      )}
      {showAboutProgram && (
        <AboutProgramModal
          darkTheme={darkTheme}
          onClose={() => setShowAboutProgram(false)}
        />
      )}
      {showTradingAccounts && (
        <TradingAccountsModal user={user} onClose={()=>setShowTradingAccounts(false)} onLogout={onLogout} onAccountSwitch={handleAccountSwitch}/>
      )}
      {toast && <Toast msg={toast} onDone={()=>setToast(null)}/>}
    </div>
  );
}